import { isLeft, isRight } from 'fp-ts/Either';
import { useState } from 'react';
import { toast } from 'sonner';
import type { TRPC } from '../../../contexts/TRPC';
import { captureAndReportError } from '../../../utils/errorReporting';

interface UseHubspotTicketNotesParams {
  ticketId: string;
  trpc: TRPC | null;
  refetchTicket: () => Promise<void>;
  onTicketUpdated?: () => Promise<void>;
}

interface UseHubspotTicketNotesResult {
  noteBody: string;
  isNoteSaving: boolean;
  noteError: string | null;
  setNoteBody: (value: string) => void;
  handleSaveNote: () => Promise<void>;
}

export function useHubspotTicketNotes({
  ticketId,
  trpc,
  refetchTicket,
  onTicketUpdated,
}: UseHubspotTicketNotesParams): UseHubspotTicketNotesResult {
  const [noteBody, setNoteBody] = useState('');
  const [isNoteSaving, setIsNoteSaving] = useState(false);
  const [noteError, setNoteError] = useState<string | null>(null);

  const handleSaveNote = async () => {
    if (!trpc?.client) {
      setNoteError('TRPC context not initialized');
      return;
    }

    const trimmed = noteBody.trim();
    if (!trimmed) {
      setNoteError('Enter a note before posting');
      return;
    }

    setIsNoteSaving(true);
    setNoteError(null);

    try {
      const result = await trpc.client.addHubspotTicketInternalNote.mutate({
        ticketId,
        body: trimmed,
      });

      if (isRight(result)) {
        setNoteBody('');
        await Promise.all([refetchTicket(), onTicketUpdated?.()]);

        toast.success('Internal note added');
      } else if (isLeft(result)) {
        setNoteError(result.left.message || 'Failed to add internal note');
        toast.error(result.left.message || 'Failed to add internal note');
      }
    } catch (err) {
      captureAndReportError('Error adding HubSpot ticket note:', err);
      setNoteError('Failed to add internal note');
      toast.error('Failed to add internal note');
    } finally {
      setIsNoteSaving(false);
    }
  };

  return {
    noteBody,
    isNoteSaving,
    noteError,
    setNoteBody,
    handleSaveNote,
  };
}
